package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.navigator
import com.siriusxm.pia.SXMUI
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Global popup manager to ensure only one popup is open at a time.
 */
object PopupManager {
    private val listeners = mutableMapOf<String, (Boolean) -> Unit>()
    private var currentOpenId: String? = null

    fun register(id: String, onStateChange: (Boolean) -> Unit) {
        listeners[id] = onStateChange
    }

    fun unregister(id: String) {
        listeners.remove(id)
    }

    fun toggle(id: String) {
        if (currentOpenId == id) {
            // Close current
            currentOpenId = null
            listeners[id]?.invoke(false)
        } else {
            // Close previous, open new
            currentOpenId?.let { oldId ->
                listeners[oldId]?.invoke(false)
            }
            currentOpenId = id
            listeners[id]?.invoke(true)
        }
    }

    fun close(id: String) {
        if (currentOpenId == id) {
            currentOpenId = null
            listeners[id]?.invoke(false)
        }
    }
}

object ButtonStyles : StyleSheet() {
    val button by style {
        position(Position.Relative)
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        gap(0.4.cssRem)
        backgroundColor(SXMUI.buttonBackgroundNormal.value())
        color(SXMUI.buttonForegroundNormal.value())
        border(0.px, LineStyle.Solid, SXMUI.buttonBorderNormal.value(Color.black))
        fontWeight("bold")
        lineHeight("1")
        padding(0.4.cssRem, 1.5.cssRem, 0.35.cssRem)
        // Trim the font's asymmetric half-leading so glyphs are optically centered.
        property("text-box-trim", "trim-both")
        property("text-box-edge", "cap alphabetic")
        cursor("pointer")
        fontSize((0.9).cssRem)

        borderRadius(10.px)
        shadowBorder()

        self + hover style {
            backgroundColor(SXMUI.buttonBackgroundNormalHover.value())
            color(SXMUI.buttonForegroundNormalHover.value())
        }
    }

    val buttonLARGE by style {
        fontSize(1.cssRem)
    }

    /** Stretches to fill the parent height — use inside a flex row with align-items: stretch. */
    val buttonINPUT_INLINE by style {
        property("align-self", "stretch")
        fontSize(1.1.cssRem)
    }

    val buttonWithPopup by style {
        paddingRight(0.7.cssRem)
    }

    val primaryButton by style {
        backgroundColor(SXMUI.buttonBackgroundPrimary.value())
        color(SXMUI.buttonForegroundPrimary.value())
        border(1.px, LineStyle.Solid, SXMUI.buttonBackgroundPrimary.value(Color.black))
        self + hover style {
            backgroundColor(SXMUI.buttonBackgroundPrimaryHover.value())
            color(SXMUI.buttonForegroundPrimary.value())
            border(1.px, LineStyle.Solid, SXMUI.buttonBackgroundPrimaryHover.value(Color.black))
        }
    }
    val disabledButton by style {
        color(SXMUI.disabledTextColor.value())
        cursor("inherit")

        border(1.px, LineStyle.Solid, SXMUI.disabledTextColor.value(Color.black))

        self + hover style {
            backgroundColor(SXMUI.buttonBackgroundNormal.value())
            color(SXMUI.disabledTextColor.value())
        }
    }

    val buttonPopup by style {
        position(Position.Absolute)
        top(100.percent)
        minWidth(100.percent)
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        shadowBorder()
        marginTop(5.px)
        overflow("hidden")
    }

    // these are used by the names are referenced with enums
    @Suppress("unused")
    val buttonPopupRight by style {
        right(0.px)
    }

    @Suppress("unused")
    val buttonPopupLeft by style {
        left(0.px)
    }

    val buttonPopupItem by style {
        display(DisplayStyle.Block)
        color(SXMUI.textColor.value())
        textAlign("left")
        whiteSpace("nowrap")
        fontWeight(500)
        cursor("pointer")
        borderRadius(7.px)

        lineHeight("1")
        padding(0.4.cssRem, 1.5.cssRem, 0.35.cssRem)
        property("text-box-trim", "trim-both")
        property("text-box-edge", "cap alphabetic")

        self + hover style {
            backgroundColor(SXMUI.buttonBackgroundPrimary.value())
            color(SXMUI.buttonForegroundPrimary.value())
        }
    }

    val buttonPopupDivider by style {
        backgroundColor(SXMUI.defaultDivider.value())
        width(100.percent)
        height(2.px)
        display(DisplayStyle.Block)
        margin(3.px, 0.px)
    }

    val roundIconButton by style {
        padding(0.px)
        borderRadius(100.percent)
        width(28.px)
        height(28.px)
        minWidth(28.px)
        minHeight(28.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }

    val flatButton by style {
        backgroundColor(SXMUI.borderDividerDefault.value())
        property("box-shadow", "none")

        self + hover style {
            backgroundColor(SXMUI.subtleTextColor.value())
            color(SXMUI.containerContentBackgroundColor.value())
        }
    }

    val framelessButton by style {
        backgroundColor(Color.transparent)
        border(0.px)
        padding(0.px)
        property("box-shadow", "none")
        cursor("pointer")

        self + hover style {
            backgroundColor(Color.transparent)
            opacity(0.7)
        }
    }
}

enum class PopupAlign {
    Left,
    Right
}

object SubActionDivider

enum class ButtonSize {
    SMALL,
    NORMAL,
    LARGE,
    INPUT_INLINE
}

/**
 * Visual style variants for buttons.
 * NORMAL: Standard button with shadow border
 * FLAT: No shadow, subtle gray background appropriate for dark/light themes
 */
enum class ButtonVariant {
    NORMAL,
    FLAT
}

/**
 * Defines the button configuration
 */
data class ButtonConfiguration(
    var title: String? = null,
    var primary: Boolean = false,
    var action: suspend () -> Unit = {}
) {
    var icon: String? = null

    var size: ButtonSize = ButtonSize.NORMAL
    var variant: ButtonVariant = ButtonVariant.NORMAL

    /**
     * When true, renders the button content without any button styling -
     * no background, border, or padding. Just the raw clickable content.
     */
    var frameless: Boolean = false

    /**
     * The scope to use for actions. This is important if the action
     * could cause the button to disappear, which would then cancel
     * processing of the action.
     */
    var scope: CoroutineScope? = null

    /**
     * When true, forces the button into a progress state with a spinner overlay.
     * Use this for externally-driven loading states where the button's own action
     * handler doesn't control the async lifecycle. Null means no external control.
     */
    var progress: Boolean? = null

    /**
     * If true, when the action is called, an indeterminate progress icon is
     * displayed on the button that will be removed when the action handler returns.
     * Since the action block is suspended, it should not return until the operation
     * is complete.
     */
    var showProgressOnAction: Boolean = false
    var popupContent: (@Composable (scope: CoroutineScope, onDismiss: () -> Unit) -> Unit)? = null
    var popupAlign: PopupAlign = PopupAlign.Left

    /**
     * Whether to use portal rendering for popups. When true, popups will be rendered
     * outside the normal DOM hierarchy to escape overflow:hidden containers.
     * Defaults to false for testing - can be enabled for better popup behavior.
     */
    var usePortal: Boolean = true

    /**
     * Position strategy for portal-rendered popups
     */
    var positionStrategy: PositionStrategy = PositionStrategy.AUTO

    var subActions = mutableListOf<Any>()

    /**
     * Renders the button as a link
     */
    var link: String? = null

    var enabled: Boolean = true

    /**
     * Is this button round?
     */
    var round: Boolean = false

    var buttonContent: (@Composable () -> Unit)? = null

    /**
     * Callback invoked when this button's popup/submenu becomes visible.
     * Only called after the popup has been opened at least once.
     */
    var onPopupShown: (() -> Unit)? = null

    /**
     * Callback invoked when this button's popup/submenu is dismissed/hidden.
     * Only called after the popup was previously shown.
     */
    var onPopupHidden: (() -> Unit)? = null

    fun action(block: suspend () -> Unit) {
        action = block
    }

    fun onPopupShown(block: () -> Unit) {
        onPopupShown = block
    }

    fun onPopupHidden(block: () -> Unit) {
        onPopupHidden = block
    }

    fun popupContent(block: @Composable (scope: CoroutineScope, onDismiss: () -> Unit) -> Unit) {
        this.popupContent = block
    }

    fun content(block: @Composable () -> Unit) {
        this.buttonContent = block
    }

    fun subAction(actionBlock: ButtonConfiguration.() -> Unit) {
        popupContent = { scope, onDismiss ->
            renderPopup(scope, onDismiss)
        }

        subActions += ButtonConfiguration().apply(actionBlock)
    }

    fun subActionDivider() {
        subActions += SubActionDivider
    }

    @Composable
    fun renderPopup(scope: CoroutineScope, onDismiss: () -> Unit) {

        Div({
            style { padding(3.px) }
        }) {
            subActions.forEach { subAction ->
                if (subAction is ButtonConfiguration) {
                    Span({
                        classes(ButtonStyles.buttonPopupItem)
                        onClick {
                            if (subAction.enabled) {
                                if (subAction.link != null) {
                                    navigator.navigate(subAction.link!!)
                                } else {
                                    scope.launch {
                                        subAction.action()
                                        onDismiss()
                                    }
                                }
                            }
                        }
                    }) {
                        if (subAction.buttonContent != null) {
                            subAction.buttonContent?.let {
                                it()
                            }

                        } else if (subAction.title != null) {
                            subAction.title?.let {
                                Text(it)
                            }
                        }
                    }
                } else if (subAction == SubActionDivider) {
                    Span({
                        classes(ButtonStyles.buttonPopupDivider)
                    })
                }
            }
        }
    }
}

/**
 * Render a button with a configuration callback block.
 */
@Composable
fun button(block: ButtonConfiguration.() -> Unit) {
    val config = ButtonConfiguration().apply(block)
    button(config)
}

/**
 * Render a button with a provided button configuration.
 */
@OptIn(ExperimentalUuidApi::class)
@Composable
fun button(config: ButtonConfiguration) {
    // Remembered so a recomposition during an in-flight action keeps the spinner and click guard.
    var isProcessingAction by remember { mutableStateOf(false) }
    val buttonId = remember { "btn_${Uuid.random()}" }
    var showPopup by remember { mutableStateOf(false) }
    var buttonElement by remember { mutableStateOf<org.w3c.dom.Element?>(null) }
    val coroutineScope = rememberCoroutineScope()
    val wasEverShown = remember { mutableStateOf(false) }

    // Register with popup manager if this button has a popup
    if (config.popupContent != null) {
        DisposableEffect(buttonId) {
            PopupManager.register(buttonId) { isOpen ->
                showPopup = isOpen
            }
            onDispose {
                PopupManager.unregister(buttonId)
            }
        }

        // Fire onPopupShown/onPopupHidden callbacks when popup visibility changes.
        // wasEverShown guards against firing onPopupHidden on initial composition.
        LaunchedEffect(showPopup) {
            if (showPopup) {
                wasEverShown.value = true
                config.onPopupShown?.invoke()
            } else if (wasEverShown.value) {
                config.onPopupHidden?.invoke()
            }
        }
    }

    Button({
        attr("type", "button")
        if (config.frameless) {
            classes(ButtonStyles.framelessButton)
        } else {
            classes("${ButtonStyles.button}${config.size.name}")
            classes(ButtonStyles.button)
            if (!config.enabled) {
                classes(ButtonStyles.disabledButton)
            } else if (config.primary) {
                classes(ButtonStyles.primaryButton)
            }
            if (config.variant == ButtonVariant.FLAT) {
                classes(ButtonStyles.flatButton)
            }
            if (config.round) {
                classes(ButtonStyles.roundIconButton)
            } else if (config.popupContent != null) {
                classes(ButtonStyles.buttonWithPopup)
            }
        }

        // Capture the button element reference for portal positioning
        ref { element ->
            buttonElement = element
            onDispose { buttonElement = null }
        }
        
        onClick {
            it.preventDefault()
            it.stopPropagation()
            if (config.enabled && !isProcessingAction) { // don't allow multiple clicks
                if (config.popupContent != null) {
                    PopupManager.toggle(buttonId)
                } else if (config.link != null) {
                    navigator.navigate(config.link!!)
                } else {
                    (config.scope ?: coroutineScope).launch {
                        isProcessingAction = true
                        config.action()
                        isProcessingAction = false
                    }
                }
            }
        }
    }) {
        Span({
            if (config.round) {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    width(100.percent)
                    height(100.percent)
                }
            }
        }) {
            if (config.buttonContent != null) {
                config.buttonContent?.invoke()
            } else {
                config.icon?.let {
                    icon(it) {
                        if (config.round) {
                            size = IconSize.SMALL
                        } else {
                            size = IconSize.TINY
                        }
                    }
                }
                config.title?.let {
                    Text(it)
                }
                config.popupContent?.let {
                    if (!config.round) {
                        icon(if (showPopup) "arrow_drop_up" else "arrow_drop_down") {
                            style {
                                lineHeight(12.px)
                            }
                        }
                    }
                }
                if (config.progress == true || (config.showProgressOnAction && isProcessingAction)) {
                    Span({
                        style {
                            position(Position.Absolute)
                            top(1.px)
                            right(1.px)
                        }
                    }) {
                        spinner(
                            style = if (config.primary) Style.LIGHT else Style.DARK,
                            size = Size.SMALL
                        )
                    }
                }
            }
        }
    }

    // Render popup using either portal or traditional positioning
    if (showPopup && config.popupContent != null) {
        if (config.usePortal) {
            // Use portal rendering for better positioning
            PositionedPortal(
                isOpen = showPopup,
                onDismiss = { PopupManager.close(buttonId) },
                triggerElement = buttonElement,
                popupAlign = config.popupAlign
            ) {
                config.popupContent?.invoke(coroutineScope) {
                    PopupManager.close(buttonId)
                }
            }
        } else {
            // Traditional relative positioning (fallback)
            Div({
                onClickOutside {
                    PopupManager.close(buttonId)
                }
                classes(ButtonStyles.buttonPopup)
                when (config.popupAlign) {
                    PopupAlign.Left -> classes(ButtonStyles.buttonPopupLeft)
                    PopupAlign.Right -> classes(ButtonStyles.buttonPopupRight)
                }
            }) {
                config.popupContent?.invoke(coroutineScope) {
                    PopupManager.close(buttonId)
                }
            }
        }
    }
}

/**
 * Simplified button rendered where a title and action is provided.
 */
@Composable
fun button(title: String, primary: Boolean = false, action: suspend () -> Unit) {
    button {
        this.title = title
        this.primary = primary
        this.action(action)
    }
}

/**
 * Configuration interface for MenuButton that allows adding menu items and setting alignment.
 */
interface MenuButtonConfig {
    /**
     * The alignment of the popup menu relative to the button.
     * Defaults to PopupAlign.Left.
     */
    var align: PopupAlign

    fun addMenuItem(block: ButtonConfiguration.() -> Unit)
}

/**
 * A button that displays a menu icon (horizontal ellipsis), and when clicked renders the child items.
 * The menu can be aligned left or right relative to the button using the align property.
 */
@Composable
fun MenuButton(config: MenuButtonConfig.() -> Unit) {
    button {
        this.round = true
        content {
            svgIcon(SVGIcons.dots) {
                size = 20.px
            }
        }

        object : MenuButtonConfig {
            override var align: PopupAlign = PopupAlign.Left
                set(value) {
                    field = value
                    this@button.popupAlign = value
                }

            override fun addMenuItem(block: ButtonConfiguration.() -> Unit) {
                this@button.subAction(block)
            }
        }.config()
    }
}
